import React, { createRef, useContext } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { clamp } from "../timeline/timeline-helper";
import {
  addTween,
  prefetchComplete,
  setPlayAll,
  updateVideoBufferStatus,
} from "../../redux/actions/appUtils";
import { PlayerAudioCallbackContext } from "./player-context";
import CanvasImage from "./canvas-image";
import TransparentVideo from "./transparent-video";
import CanvasVideo from "./canvas-video";
import { addCacheClearQuery } from "../../helper/addCacheClearQuery";

class PlayerMediaComponent extends React.Component {
  constructor(props) {
    super(props);

    this.changeMediaTime = this.changeMediaTime.bind(this);
    this.isTimePropChanged = this.isTimePropChanged.bind(this);
    this.checkMediaHasEnoughData = this.checkMediaHasEnoughData.bind(this);
    this.handleBuffering = this.handleBuffering.bind(this);
    this.prefetchMedia = this.prefetchMedia.bind(this);
    this.play = this.play.bind(this);
    this.pause = this.pause.bind(this);
    this.playPauseTweenCallback = this.playPauseTweenCallback.bind(this);
    this.handleLoop = this.handleLoop.bind(this);
    this.handleError = this.handleError.bind(this);
    this.assignRef = this.assignRef.bind(this);
    this._preSeekForSceneTransition = this._preSeekForSceneTransition.bind(this);
    this.handleVideoSeeked = this.handleVideoSeeked.bind(this);

    this.mediaElRef = createRef(null);
    this.imageRef = createRef(null);

    this.reverseInterval = 1000 / 30; // ms
    this.reverseTimer = null;

    this.mediaPrefetchTimer = null;
    this.prefetchCheckInterval = 50; // ms
    this.prefetchTries = 100;
    this._prefetchCanplayHandler = null;

    this.bufferStartMediaTime = null;
    this.bufferCheckTimer = null;
    this.bufferCheckInterval = 200; // ms

    this._seekWaitInterval = null;
    this._seekSafetyTimeout = null;

    this.preload = null;
    this._isBufferingRequested = false;
    this._pauseTimeout = null;
    this._pendingSeekTime = null;

    // FIX 1: proximity buffer constant — media outside this window is completely skipped
    this._NEAR_BUFFER = 0.5; // seconds
    // FIX 3: seek tolerance to prevent micro-seek loops
    this._SEEK_THRESHOLD = 0.08; // seconds
    this._prefetchedToken = null;

    // AI Reframe optimization: debounce buffering requests
    this._bufferDebounceTimer = null;
    this._lastBufferRequestTime = 0;
    this._MIN_BUFFER_INTERVAL = 300; // ms - minimum time between buffer requests
    this._MIN_BUFFER_INTERVAL_REFRAME = 1500; // ms — longer for reframe to prevent cascade stalls
    this._idleSeekDebounceTimer = null;
  }


  componentDidMount() {
    if (this.props.item?.get?.("skipVideoElement")) {
      if (this.props.isPlaying) {
        this.props.prefetchComplete(this.props.prefetchToken);
      }
      return;
    }

    const { runningState } = this.props;

    let { playhead } = this.props;
    if (this.props.isPlaying) {
      playhead = runningState.get("seekPlayhead");
    }

    // FIX 1: Only seek on mount if we're near the play range
    let shouldSeek = this._isNearPlayRange(playhead);
    if (shouldSeek && this._isReframeScene()) {
      const { playAt, pauseAt } = this.props;
      if (playhead < playAt || playhead >= pauseAt) {
        shouldSeek = false;
      }
    }
    if (shouldSeek) {
      this.changeMediaTime(playhead);
    }

    if (this.props.isPlaying) {
      this.prefetchMedia();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.item?.get?.("skipVideoElement")) return;

    const isPlayStarted = !prevProps.isPlaying && this.props.isPlaying;
    const isPlayEnded = prevProps.isPlaying && !this.props.isPlaying;
    const isPlayerLoaded = !prevProps.isLoaded && this.props.isLoaded;
    const isSeeked = this.props.runningState.get("seekToken") !== prevProps.runningState.get("seekToken");
    const isItemChanged = this.props.item && prevProps.item !== this.props.item;
    const isIdleTimeChanged = !this.props.isPlaying && this.isTimePropChanged(prevProps, this.props);

    const isBuffering = this.props.videoBufferStatus.size > 0;
    const isCurrentMediaBuffering = this.props.videoBufferStatus.has(this.props.id);

    // FIX: Force pause if buffering started and video is somehow playing
    const wasBuffering = prevProps.videoBufferStatus.size > 0;
    if (!wasBuffering && isBuffering && this.mediaElRef.current && !this.mediaElRef.current.paused) {
      this.mediaElRef.current.pause();
    }

    const SMART_FOCUS_COMPONENTS = ["SMART_FOCUS", "SMART_FOCUS_MOBILE"];
    const wasSmartFocus = SMART_FOCUS_COMPONENTS.includes(prevProps.propertyComponent);
    const isSmartFocus = SMART_FOCUS_COMPONENTS.includes(this.props.propertyComponent);
    const propertyComponentChanged = wasSmartFocus && !isSmartFocus;

    if (propertyComponentChanged && !this.props.isPlaying) {
      // FIX 1: Only re-seek for zoom preview if near the play range
      let shouldSeek = this._isNearPlayRange(this.props.playhead);
      if (shouldSeek && this._isReframeScene()) {
        const { playAt, pauseAt } = this.props;
        if (this.props.playhead < playAt || this.props.playhead >= pauseAt) {
          shouldSeek = false;
        }
      }
      if (shouldSeek) {
        this.changeMediaTime(this.props.playhead);
      }
    }

    if (isPlayStarted) {
      const seekPlayhead = this.props.runningState.get("seekPlayhead");
      // FIX 1: Skip inactive media entirely on play start
      if (!this._isNearPlayRange(seekPlayhead)) return;
      this.changeMediaTime(seekPlayhead);
      this.prefetchMedia();
    } else if (isSeeked || isPlayerLoaded || (isItemChanged && this.props.isPlaying && this.props.isLoaded)) {
      if (isSeeked) {
        if (this.props.fadeOutAudio) {
          this.props.fadeOutAudio({ id: this.props.id });
        }
      }

      const playhead = this.props.runningState.get("seekPlayhead");
      const isWithinPlayRange = playhead >= this.props.playAt && playhead < this.props.pauseAt;

      // FIX 1: If not near the play range at all, skip all seek/pause operations
      if (!this._isNearPlayRange(playhead)) {
        // Clean up buffering state if we were buffering
        if (isCurrentMediaBuffering) {
          clearInterval(this.bufferCheckTimer);
          this._isBufferingRequested = false;
          this.props.updateVideoBufferStatus({ videoKey: this.props.id, isBuffering: false, videoSrc: this._baseVideoSrc() });
        }
        return;
      }

      if (this.props.isPlaying) {
        this.prefetchMedia();
      }

      if (!isWithinPlayRange || this.props.isPaused) {
        this.pause(playhead);
        if (isCurrentMediaBuffering) {
          clearInterval(this.bufferCheckTimer);
          this._isBufferingRequested = false;
          this.props.updateVideoBufferStatus({ videoKey: this.props.id, isBuffering: false, videoSrc: this._baseVideoSrc() });
        }
      } else if (isBuffering && !isCurrentMediaBuffering) {
        this.pause(playhead);
      } else if (isWithinPlayRange && !isBuffering) {
        this.play(playhead);
      }
    } else if (isIdleTimeChanged) {
      if (this._isReframeScene()) {
        const { playAt, pauseAt } = this.props;
        if (this.props.playhead >= playAt && this.props.playhead < pauseAt) {
          clearTimeout(this._idleSeekDebounceTimer);
          this._idleSeekDebounceTimer = setTimeout(() => {
            const { playhead, playAt: latestPlayAt, pauseAt: latestPauseAt } = this.props;
            if (playhead >= latestPlayAt && playhead < latestPauseAt) {
              this.changeMediaTime(playhead);
            }
          }, 80);
        }
      } else if (this._isNearPlayRange(this.props.playhead)) {
        this.changeMediaTime(this.props.playhead);
      }
    }

    if (isPlayEnded || isPlayerLoaded) {
      clearInterval(this.bufferCheckTimer);
      clearInterval(this.mediaPrefetchTimer);
      if (this._prefetchCanplayHandler && this.mediaElRef.current) {
        this.mediaElRef.current.removeEventListener("canplay", this._prefetchCanplayHandler);
        this._prefetchCanplayHandler = null;
      }
      if (isPlayEnded) {
        // FIX 2: Only pause if the video is actually playing
        if (this.mediaElRef.current && !this.mediaElRef.current.paused) {
          this.pause(this.props.playhead);
        }
      }
    }
  }

  componentWillUnmount() {
    clearInterval(this.bufferCheckTimer);
    clearInterval(this.mediaPrefetchTimer);
    clearInterval(this.reverseTimer);
    clearInterval(this._seekWaitInterval);
    clearTimeout(this._seekSafetyTimeout);
    clearInterval(this._backSeekBufferTimer);
    clearInterval(this._playbackMonitorTimer);
    clearTimeout(this._bufferDebounceTimer);
    clearTimeout(this._idleSeekDebounceTimer);

    if (this._prefetchCanplayHandler && this.mediaElRef.current) {
      this.mediaElRef.current.removeEventListener("canplay", this._prefetchCanplayHandler);
      this._prefetchCanplayHandler = null;
    }

    if (this.mediaElRef.current) {
      this.mediaElRef.current.removeEventListener("seeked", this.handleVideoSeeked);
      // FIX 2: Only call pause() if actually playing
      if (!this.mediaElRef.current.paused) {
        this.mediaElRef.current.pause();
      }
      this.mediaElRef.current.src = "";
      this.mediaElRef.current.removeAttribute("src");
      this.mediaElRef.current.load();
    }
  }

  handleBuffering() {
    try {
      const currentPlayhead = this.props.t1.time();

      // FIX 1: Skip buffering logic entirely if not near play range
      if (!this._isNearPlayRange(currentPlayhead)) return;

      // AI Reframe optimization: Skip frequent buffering for reframe scenes
      if (this._shouldSkipBuffering()) return;

      const { currentTime } = this.mediaElRef.current;
      const isWithinPlayRange = this.props.playAt <= currentPlayhead && currentPlayhead < this.props.pauseAt;

      const isBuffering = this.props.videoBufferStatus.size > 0;
      const isCurrentVideoBuffering = (isBuffering && this.props.videoBufferStatus.has(this.props.id)) || this._isBufferingRequested;

      const checkParams = { useReadyState: false, currentTime };
      const hasEnoughData = this.checkMediaHasEnoughData(checkParams);
      const sameSrcBuffering = this._sameSrcAlreadyBuffering();

      if (this.props.isLoaded && !isCurrentVideoBuffering && isWithinPlayRange
        && !hasEnoughData && !this.props.isPaused && !sameSrcBuffering) {
        // AI Reframe optimization: Update last buffer request time
        this._lastBufferRequestTime = Date.now();
        const videoSrc = this._baseVideoSrc();
        this._isBufferingRequested = true;
        this.props.updateVideoBufferStatus({
          videoKey: this.props.id,
          isBuffering: true,
          bufferStartedAt: currentPlayhead,
          currentTime,
          videoSrc,
        });

        // FIX: Pause the video immediately when buffering starts
        if (this.mediaElRef.current && !this.mediaElRef.current.paused) {
          this.mediaElRef.current.pause();
        }

        this.bufferStartMediaTime = currentTime;
        clearInterval(this.bufferCheckTimer);
        const isReframeScene = this._isReframeScene();
        // AI Reframe optimization: Use longer check interval to reduce overhead
        const checkInterval = isReframeScene ? 200 : this.bufferCheckInterval;
        this.bufferCheckTimer = setInterval(() => {
          try {
            const buffered = this.checkMediaHasEnoughData({
              ...checkParams,
              useReadyState: this._isReframeScene(),
              requiredReadyState: HTMLMediaElement.HAVE_ENOUGH_DATA,
            });
            if (buffered) {
              this.bufferStartMediaTime = null;
              clearInterval(this.bufferCheckTimer);
              this._isBufferingRequested = false;
              this.props.updateVideoBufferStatus({
                videoKey: this.props.id,
                isBuffering: false,
                videoSrc: this._baseVideoSrc(),
              });

              // FIX: Resume playback after buffering completes if still within play range
              const currentPlayhead = this.props.t1 ? this.props.t1.time() : 0;
              const isWithinPlayRange = this.props.playAt <= currentPlayhead && currentPlayhead < this.props.pauseAt;
              const shouldBePlaying = this.props.isPlaying && !this.props.isPaused && isWithinPlayRange;

              if (shouldBePlaying && this.mediaElRef.current && this.mediaElRef.current.paused) {
                // Check if no other videos are buffering before resuming
                setTimeout(() => {
                  const stillNoBuffering = this.props.videoBufferStatus.size === 0;
                  if (stillNoBuffering && this.mediaElRef.current && this.mediaElRef.current.paused) {
                    const targetRate = Number.isFinite(this.props.speed) && this.props.speed > 0 ? this.props.speed : 1;
                    this.mediaElRef.current.playbackRate = targetRate;
                    this.mediaElRef.current.play().catch(() => { });
                  }
                }, this._isReframeScene() ? 200 : 50);
              }
            } else {
              const isBuffering = this.props.videoBufferStatus.size > 0;
              const { currentTime, paused } = this.mediaElRef.current;
              const isCurrentVideoBuffering = (isBuffering && this.props.videoBufferStatus.has(this.props.id)) || this._isBufferingRequested;

              if (isCurrentVideoBuffering && Number.isFinite(this.bufferStartMediaTime)) {
                // FIX: Ensure video stays paused during buffering
                // Only try webkit workaround if buffer is making progress
                if (this.props.reverse) {
                  if (this.bufferStartMediaTime - currentTime >= 3) {
                    if (!paused) {
                      this.mediaElRef.current.pause();
                    }
                  }
                  // Removed webkit play() call - keep video paused during buffering
                } else if (currentTime - this.bufferStartMediaTime >= 1) {
                  if (!paused) {
                    this.mediaElRef.current.pause();
                  }
                }
                // Removed webkit play() call - keep video paused during buffering
                // The video will only resume after buffering completes (see lines 262-277)
              }
            }
          } catch (error) { }
        }, checkInterval);
      }
    } catch (error) { }
  }

  handleError() {
    try {
      if (this.props.isPlaying) {
        const src = addCacheClearQuery(this.props.src, this.props.allowCustomCaching, this.props.reverse);
        if (this.mediaElRef.current && this.mediaElRef.current.src === src) {
          if (this.props.isPlayAll) {
            this.props.setPlayAll(false);
          }
        }
      }
    } catch (error) { }
  }

  handleLoop() {
    try {
      // FIX: Check buffering status first - don't trigger playback if buffering
      const isBuffering = this.props.videoBufferStatus.size > 0;
      if (isBuffering) return;

      if (this.props.isPlaying && this.props.loop && this.mediaElRef.current && !this.props.isPaused) {
        const playhead = this.props.t1.time();
        const { currentTime, paused } = this.mediaElRef.current;
        const isWithinPlayRange = this.props.playAt <= playhead && playhead < this.props.pauseAt;
        const isWithinMediaRange = this.props.mediaStart < currentTime && currentTime < this.props.mediaEnd;

        if (isWithinPlayRange && (!isWithinMediaRange || paused)) {
          this.playPauseTweenCallback(false);
        }
      }
    } catch (error) { }
  }

  handleVideoSeeked() {
    try {
      if (!this.props.isPlaying && this._pendingSeekTime !== null) {
        const targetTime = this._pendingSeekTime;
        this._pendingSeekTime = null;
        if (this.mediaElRef.current) {
          this.mediaElRef.current.currentTime = targetTime;
        }
      }
    } catch (e) { }
  }

  // ─── FIX 1: Central guard — returns true only when this media is near enough to act on ───
  _isNearPlayRange(playhead) {
    const { playAt, pauseAt } = this.props;
    return (
      playhead >= playAt - this._NEAR_BUFFER &&
      playhead <= pauseAt + this._NEAR_BUFFER
    );
  }

  // AI Reframe optimization: check if this is a reframe scene
  _isReframeScene() {
    return this.props.item?.get?.("sceneData") && this.props.item?.get?.("originalId");
  }

  // AI Reframe optimization: check if same source video is already buffering
  _isSameSourceBuffering() {
    try {
      if (!this._isReframeScene()) return false;

      const baseSrc = this._baseVideoSrc();
      if (!baseSrc) return false;

      const bufferStatus = this.props.videoBufferStatus;
      let foundBuffering = false;

      bufferStatus.forEach((val, key) => {
        if (key !== this.props.id && val && val.get && val.get("videoSrc") === baseSrc) {
          foundBuffering = true;
        }
      });

      return foundBuffering;
    } catch (e) {
      return false;
    }
  }

  // AI Reframe optimization: debounce buffer requests
  _shouldSkipBuffering() {
    if (!this._isReframeScene()) return false;

    // Skip if same source is already buffering
    if (this._isSameSourceBuffering()) return true;

    // Skip if buffered too recently (debouncing)
    const now = Date.now();
    const minInterval = this._isReframeScene()
      ? this._MIN_BUFFER_INTERVAL_REFRAME
      : this._MIN_BUFFER_INTERVAL;
    if (now - this._lastBufferRequestTime < minInterval) {
      return true;
    }

    return false;
  }

  _startPlaybackMonitor() {
    clearInterval(this._playbackMonitorTimer);
    let lastPlayhead = null;
    let lastVideoTime = null;
    const monitorStartTime = Date.now();

    // AI Reframe optimization: Use longer monitor interval for reframe scenes
    const isReframeScene = this._isReframeScene();
    const monitorInterval = isReframeScene ? 200 : 100;

    this._playbackMonitorTimer = setInterval(() => {
      if (!this.props.isPlaying || this.props.isPaused) return;
      if (!this.mediaElRef.current) return;

      const playhead = this.props.t1 ? this.props.t1.time() : 0;

      // FIX 1: Stop monitor if drifted out of near range
      if (!this._isNearPlayRange(playhead)) return;

      const isWithinPlayRange = this.props.playAt <= playhead && playhead < this.props.pauseAt;
      if (!isWithinPlayRange) return;

      // FIX: Force pause if buffering and video is somehow playing
      const isBuffering = this.props.videoBufferStatus.size > 0;
      if (isBuffering && this.mediaElRef.current && !this.mediaElRef.current.paused) {
        this.mediaElRef.current.pause();
        return;
      }

      const { readyState, seeking, paused, currentTime } = this.mediaElRef.current;
      const isStarved = readyState < HTMLMediaElement.HAVE_CURRENT_DATA;

      let isFrozen = false;
      const monitorAge = Date.now() - monitorStartTime;
      if (monitorAge > 2000 && lastPlayhead !== null && lastVideoTime !== null && !seeking && !paused) {
        const playheadDelta = playhead - lastPlayhead;
        const videoDelta = currentTime - lastVideoTime;
        const isRateZero = this.mediaElRef.current.playbackRate === 0;
        if (!isRateZero && playheadDelta > 0.05 && videoDelta < 0.005) {
          isFrozen = true;
        }
      }

      lastPlayhead = playhead;
      lastVideoTime = currentTime;

      if ((isStarved || isFrozen) && !seeking && !paused) {
        this.handleBuffering();
      }
    }, monitorInterval);
  }

  _stopPlaybackMonitor() {
    clearInterval(this._playbackMonitorTimer);
  }

  applyZoomPreview(item, segment) {
    try {
      if (!segment) return;

      const wsItem = (item?.get) ? item : this.props.item;
      const { projectData, zoomFactor = 1 } = this.props;

      if (!wsItem?.get || !projectData?.get) return;

      const id = wsItem.get("id");
      if (!id) return;

      const overlayDiv = document.getElementById(`${id}animo`);
      if (!overlayDiv) return;

      const itemContainer = document.getElementById(id);
      if (itemContainer) {
        itemContainer.style.overflow = "visible";
      }

      const projectWidth = projectData.get("width");
      const projectHeight = projectData.get("height");
      if (!Number.isFinite(projectWidth) || !Number.isFinite(projectHeight)) return;

      const validZoomFactor = Number.isFinite(zoomFactor) ? zoomFactor : 1;
      const itemX = wsItem.get("x") || 0;
      const itemY = wsItem.get("y") || 0;
      const zoomW = segment.width || projectWidth;
      const zoomH = segment.height || projectHeight;
      const zoomX = segment.x || 0;
      const zoomY = segment.y || 0;

      const dx = zoomX - itemX;
      const dy = zoomY - itemY;
      const scaledProjWidth = projectWidth * validZoomFactor;
      const scaledProjHeight = projectHeight * validZoomFactor;
      const zScaleX = scaledProjWidth / (zoomW * validZoomFactor);
      const zScaleY = scaledProjHeight / (zoomH * validZoomFactor);
      const correctedWidth = dx * validZoomFactor;
      const correctedHeight = dy * validZoomFactor;
      const zX = zScaleX * correctedWidth * -1;
      const zY = zScaleY * correctedHeight * -1;
      const tweenX = zX - (itemX * validZoomFactor);
      const tweenY = zY - (itemY * validZoomFactor);

      overlayDiv.style.transformOrigin = "0px 0px";
      overlayDiv.style.transform = `translate(${tweenX}px, ${tweenY}px) scale(${zScaleX}, ${zScaleY})`;
    } catch (e) { }
  }

  clearZoomPreview(item) {
    try {
      const wsItem = (item?.get) ? item : this.props.item;
      if (!wsItem?.get) return;

      const id = wsItem.get("id");
      if (!id) return;

      const overlayDiv = document.getElementById(`${id}animo`);
      if (!overlayDiv) return;

      overlayDiv.style.transform = "";
      overlayDiv.style.transformOrigin = "";

      const itemContainer = document.getElementById(id);
      if (itemContainer) {
        itemContainer.style.overflow = "";
      }
    } catch (e) { }
  }

  zoomForCurrentTime(currentTime) {
    try {
      const { item } = this.props;
      if (!item || !item.get) return;

      const zoomDataImmutable = item.get("zoomData");
      const zoomData = zoomDataImmutable.toJS ? zoomDataImmutable.toJS() : zoomDataImmutable;
      if (!zoomData || Object.keys(zoomData).length === 0) return;

      const activeSegment = Object.values(zoomData).reduce((found, zoomGroup) => {
        if (found || !zoomGroup || typeof zoomGroup !== "object") return found;

        const { zoomIn, zoomOut } = zoomGroup;
        const start = zoomIn?.start;
        const end = zoomOut?.end;

        if (typeof start !== "number" || typeof end !== "number") return found;
        if (currentTime < start || currentTime > end) return found;

        if (zoomOut?.start !== undefined && zoomOut?.end !== undefined
          && currentTime >= zoomOut.start && currentTime <= zoomOut.end) {
          return zoomOut;
        }
        if (zoomIn) {
          return zoomIn;
        }
        return found;
      }, null);

      if (activeSegment) {
        this.applyZoomPreview(item, activeSegment);
      } else {
        this.clearZoomPreview(item);
      }
    } catch (e) { }
  }

  assignRef(r) {
    if (this.mediaElRef.current) {
      this.mediaElRef.current.removeEventListener("seeked", this.handleVideoSeeked);
    }
    this.mediaElRef.current = r;
    if (r) {
      r.volume = 0;
      r.addEventListener("seeked", this.handleVideoSeeked);
    }
    if (typeof this.props.mediaElRef === "function") {
      this.props.mediaElRef(r, this.props.id);
    } else if (this.props.mediaElRef && typeof this.props.mediaElRef === "object") {
      this.props.mediaElRef.current = r;
    }
  }

  changeMediaTime(playhead, timeProps = this.props) {
    try {
      const { playStart, playEnd, mediaStart, mediaEnd, loop = false, reverse = false } = timeProps;
      let { speed = 1 } = timeProps;
      const isReframeScene = this._isReframeScene();

      if (this.props.isPlaying) {
        // AI Reframe optimization: Use tighter range buffer to reduce processing
        const rangeBuffer = isReframeScene ? 3 : 20;
        const isWithinItemRange = playStart - rangeBuffer <= playhead && playhead <= playEnd + rangeBuffer;
        if (!isWithinItemRange) return;
      }

      if (this._justStartedPlaying) return;

      if (!Number.isFinite(speed)) speed = 1;

      const loopDuration = (mediaEnd - mediaStart) / speed;
      let loopPlayhead = playhead - playStart;
      if (loop) {
        const currentLoop = Math.max(Math.floor(loopPlayhead / loopDuration), 0);
        const currentLoopStart = currentLoop * loopDuration;
        loopPlayhead -= currentLoopStart;
      }
      let currentTime = clamp(mediaStart + loopPlayhead * speed, mediaStart, mediaEnd);
      if (reverse) {
        currentTime = mediaEnd - (currentTime - mediaStart);
      }

      if (this.mediaElRef.current) {
        this.mediaElRef.current.playbackRate = speed;

        // AI Reframe optimization: Use larger seek threshold for reframe scenes to avoid micro-seeks
        const seekThreshold = isReframeScene ? 0.15 : this._SEEK_THRESHOLD;
        const currentVideoTime = this.mediaElRef.current.currentTime;
        if (Math.abs(currentVideoTime - currentTime) > seekThreshold) {
          if (!this.props.isPlaying && isReframeScene) {
            const isActiveScene = playhead >= this.props.playAt && playhead < this.props.pauseAt;
            if (isActiveScene) {
              if (this.mediaElRef.current.seeking) {
                this._pendingSeekTime = currentTime;
              } else {
                this._pendingSeekTime = null;
                this.mediaElRef.current.currentTime = currentTime;
              }
            }
          } else if (!this.props.isPlaying && this.mediaElRef.current.seeking) {
            this._pendingSeekTime = currentTime;
          } else {
            this._pendingSeekTime = null;
            this.mediaElRef.current.currentTime = currentTime;
          }
        }
      }

      const SMART_FOCUS_COMPONENTS = ["SMART_FOCUS", "SMART_FOCUS_MOBILE"];
      if (!this.props.isPlaying) {
        const isActiveScene = playhead >= this.props.playAt && playhead < this.props.pauseAt;
        if (isReframeScene && !isActiveScene) {
          // Skip zoom preview computation for non-active reframe scenes
        } else if (!SMART_FOCUS_COMPONENTS.includes(this.props.propertyComponent) || this.props.isMobileView) {
          this.zoomForCurrentTime(currentTime);
        } else {
          this.clearZoomPreview(this.props.item);
        }
      }

      const { duration } = this.mediaElRef.current;
      if (this.props.isPlaying && reverse && Number.isFinite(duration)
        && this.props.swStatus === "fulfilled" && navigator.serviceWorker.controller) {
        const range = 60 / speed;
        const reqInterval = 10;
        const preloadReqTime = Math.min(Math.ceil(currentTime / reqInterval) * reqInterval, duration);

        if (this.lastPreloadReqTime !== preloadReqTime && preloadReqTime > 0) {
          navigator.serviceWorker.controller.postMessage({
            type: "FETCH_MEDIA_BY_TIME",
            data: {
              startTime: Math.max(preloadReqTime - range, 0),
              endTime: preloadReqTime,
              duration,
              src: this.mediaElRef.current.src,
            },
          });
          this.lastPreloadReqTime = preloadReqTime;
        }
      }
    } catch (error) {
      if (this.props.isPlayAll) {
        this.props.setPlayAll(false);
      }
    }
  }

  play(playhead) {
    try {
      clearTimeout(this._idleSeekDebounceTimer);
      if (!this._isNearPlayRange(playhead)) return;

      this._pendingSeekTime = null; // Clear any pending seek when starting playback

      // FIX: Don't attempt play if any video is currently buffering
      const isBuffering = this.props.videoBufferStatus.size > 0;
      if (isBuffering) return;

      if (this.props.fadeOutAudio) {
        this.props.fadeOutAudio({ id: this.props.id });
      }

      if (this.props.playPauseAudio) {
        this.props.playPauseAudio({
          id: this.props.id,
          playhead,
          mediaType: this.props.mediaType,
          forceRestart: true, // 👈 add this flag
        });
      }

      clearTimeout(this._pauseTimeout);
      if (this.mediaElRef.current.preload !== "auto") {
        this.preload = "auto";
        this.mediaElRef.current.preload = "auto";
        this.mediaElRef.current.src = addCacheClearQuery(this.props.src, this.props.allowCustomCaching, this.props.reverse);
        this.mediaElRef.current.load();
      }

      clearInterval(this.reverseTimer);

      const { mediaStart, mediaEnd, playStart, speed = 1, loop = false, reverse = false } = this.props;
      const loopDuration = (mediaEnd - mediaStart) / (Number.isFinite(speed) ? speed : 1);
      let loopPlayhead = playhead - playStart;
      if (loop) {
        const currentLoop = Math.max(Math.floor(loopPlayhead / loopDuration), 0);
        loopPlayhead -= currentLoop * loopDuration;
      }
      let expectedTime = clamp(mediaStart + loopPlayhead * (Number.isFinite(speed) ? speed : 1), mediaStart, mediaEnd);
      if (reverse) {
        expectedTime = mediaEnd - (expectedTime - mediaStart);
      }
      const currentVideoTime = this.mediaElRef.current.currentTime;

      const isStalledOrPaused = this.mediaElRef.current.paused || this.mediaElRef.current.playbackRate === 0;
      const frameAlreadyReady = this._frameReady === true;
      const seekThreshold = frameAlreadyReady ? 0.2 : (isStalledOrPaused ? 0.1 : 0.1);

      // FIX 3: Honour configurable seek threshold — use the larger of the two thresholds
      // so that the frame-ready and micro-seek protections both apply
      const effectiveThreshold = Math.max(seekThreshold, this._SEEK_THRESHOLD);
      const needsSeek = !Number.isFinite(currentVideoTime) || Math.abs(currentVideoTime - expectedTime) > effectiveThreshold;
      const isSignificantSeek = needsSeek && Number.isFinite(currentVideoTime) && Math.abs(currentVideoTime - expectedTime) > 0.5;

      if (needsSeek) {
        this.changeMediaTime(playhead);
      }

      if (this.mediaElRef.current &&
        !this.mediaElRef.current.paused &&
        this.mediaElRef.current.playbackRate === 0) {
        const targetRate = Number.isFinite(this.props.speed) && this.props.speed > 0
          ? this.props.speed : 1;
        this.mediaElRef.current.playbackRate = targetRate;
      }

      const isCurrentlyBuffering = this.props.videoBufferStatus.has(this.props.id) || this._isBufferingRequested;
      // AI Reframe optimization: Skip buffering on significant seek for reframe scenes if same source is buffering
      const shouldSkipSeekBuffer = this._isReframeScene() && this._isSameSourceBuffering();

      if (isSignificantSeek && this.props.isLoaded && !isCurrentlyBuffering && !shouldSkipSeekBuffer) {
        const videoSrc = this._baseVideoSrc();
        this._isBufferingRequested = true;
        this._lastBufferRequestTime = Date.now();
        this.props.updateVideoBufferStatus({
          videoKey: this.props.id,
          isBuffering: true,
          bufferStartedAt: playhead,
          videoSrc,
        });

        // FIX: Pause the video immediately when buffering starts
        if (this.mediaElRef.current && !this.mediaElRef.current.paused) {
          this.mediaElRef.current.pause();
        }
        const clearBufferOnReady = () => {
          if (!this.mediaElRef.current) return;
          const { readyState, seeking } = this.mediaElRef.current;
          // FIX: More conservative check - require HAVE_ENOUGH_DATA and ensure not seeking
          if (readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA && !seeking) {
            clearInterval(this._backSeekBufferTimer);
            this._backSeekBufferTimer = null;
            this._isBufferingRequested = false;
            this.props.updateVideoBufferStatus({
              videoKey: this.props.id,
              isBuffering: false,
              videoSrc: this._baseVideoSrc(),
            });

            // FIX: Resume playback after buffering completes if still within play range
            const currentPlayhead = this.props.t1 ? this.props.t1.time() : 0;
            const isWithinPlayRange = this.props.playAt <= currentPlayhead && currentPlayhead < this.props.pauseAt;
            const shouldBePlaying = this.props.isPlaying && !this.props.isPaused && isWithinPlayRange;

            if (shouldBePlaying && this.mediaElRef.current && this.mediaElRef.current.paused) {
              // Check if no other videos are buffering before resuming
              setTimeout(() => {
                const stillNoBuffering = this.props.videoBufferStatus.size === 0;
                if (stillNoBuffering && this.mediaElRef.current && this.mediaElRef.current.paused) {
                  const targetRate = Number.isFinite(this.props.speed) && this.props.speed > 0 ? this.props.speed : 1;
                  this.mediaElRef.current.playbackRate = targetRate;
                  this.mediaElRef.current.play().catch(() => { });
                }
              }, 50);
            }
          }
        };
        clearInterval(this._backSeekBufferTimer);
        this._backSeekBufferTimer = setInterval(clearBufferOnReady, 50);
        // Strong seek fetch: reframe scenes get longer safety timeout to ensure
        // HAVE_ENOUGH_DATA is reached before resuming, preventing frequent re-buffer cycles
        const seekSafetyDuration = this._isReframeScene() ? 5000 : 2000;
        setTimeout(() => {
          if (this._backSeekBufferTimer) {
            clearInterval(this._backSeekBufferTimer);
            this._backSeekBufferTimer = null;
            this._isBufferingRequested = false;
            this.props.updateVideoBufferStatus({
              videoKey: this.props.id,
              isBuffering: false,
              videoSrc: this._baseVideoSrc(),
            });
          }
        }, seekSafetyDuration);
      }

      if (this.props.reverse) {
        this.reverseTimer = setInterval(() => {
          try {
            const playhead = this.props.t1.time();
            this.changeMediaTime(playhead);
          } catch (error) { }
        }, this.reverseInterval);
      } else {
        const currentPlayhead = this.props.t1 ? this.props.t1.time() : playhead;
        const isActiveScene = currentPlayhead >= this.props.playAt && currentPlayhead < this.props.pauseAt;

        if (!isActiveScene) return;

        clearInterval(this._seekWaitInterval);

        const attemptPlay = () => {
          if (!this.mediaElRef.current) return;

          // FIX: Don't attempt play if any video is buffering
          const isBuffering = this.props.videoBufferStatus.size > 0;
          if (isBuffering) return;

          const { readyState, seeking, paused } = this.mediaElRef.current;

          // FIX 4: Guard — don't attempt play if not enough data
          if (readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;

          const hasEnoughData = !seeking;
          const frameReady = this._frameReady !== false;
          const pollAge = Date.now() - (this._attemptPlayStartTime || Date.now());
          const frameReadyOrTimeout = frameReady || pollAge > 500;

          if (hasEnoughData && frameReadyOrTimeout) {
            clearInterval(this._seekWaitInterval);
            clearTimeout(this._seekSafetyTimeout);
            this._frameReady = undefined;

            const finalizePlay = () => {
              if (this._wasBufferingForPlay) {
                this._wasBufferingForPlay = false;
                this._isBufferingRequested = false;
                this.props.updateVideoBufferStatus({
                  videoKey: this.props.id,
                  isBuffering: false,
                  videoSrc: this._baseVideoSrc(),
                });
              }
            };

            // FIX 2 + FIX 4: Only call play() if paused AND readyState is sufficient
            if (paused && readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
              const targetRate = Number.isFinite(this.props.speed) && this.props.speed > 0 ? this.props.speed : 1;
              if (this.mediaElRef.current.playbackRate !== targetRate) {
                this.mediaElRef.current.playbackRate = targetRate;
              }

              this._justStartedPlaying = true;
              this._startPlaybackMonitor();
              this.mediaElRef.current.play().then(() => {
                finalizePlay();
                setTimeout(() => { this._justStartedPlaying = false; }, 32);
              }).catch(() => {
                finalizePlay();
                this._justStartedPlaying = false;
              });
            } else {
              finalizePlay();
            }
          }
        };

        const { readyState, seeking, paused } = this.mediaElRef.current;
        const frameReady = this._frameReady !== false;

        // FIX: Don't attempt immediate play if any video is buffering
        const isBuffering = this.props.videoBufferStatus.size > 0;
        if (isBuffering) {
          // Wait for buffering to complete
          return;
        }

        // FIX 4: Check readyState >= HAVE_CURRENT_DATA before immediate play attempt
        const hasEnoughDataNow = readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && !seeking;

        if (hasEnoughDataNow && frameReady && paused) {
          this._frameReady = undefined;

          const targetRate = Number.isFinite(this.props.speed) && this.props.speed > 0 ? this.props.speed : 1;
          if (this.mediaElRef.current.playbackRate !== targetRate) {
            this.mediaElRef.current.playbackRate = targetRate;
          }

          this._justStartedPlaying = true;
          this._startPlaybackMonitor();

          let playResolved = false;
          const stallTimer = setTimeout(() => {
            if (!playResolved && !this._isBufferingRequested) {
              this._isBufferingRequested = true;
              this._wasBufferingForPlay = true;
              this.props.updateVideoBufferStatus({
                videoKey: this.props.id,
                isBuffering: true,
                bufferStartedAt: playhead,
                videoSrc: this._baseVideoSrc(),
              });

              // FIX: Pause the video immediately when buffering starts
              if (this.mediaElRef.current && !this.mediaElRef.current.paused) {
                this.mediaElRef.current.pause();
              }
            }
          }, 50);

          this.mediaElRef.current.play().then(() => {
            playResolved = true;
            clearTimeout(stallTimer);
            if (this._wasBufferingForPlay) {
              this._wasBufferingForPlay = false;
              this._isBufferingRequested = false;
              this.props.updateVideoBufferStatus({
                videoKey: this.props.id,
                isBuffering: false,
                videoSrc: this._baseVideoSrc(),
              });
            }

            setTimeout(() => {
              this._justStartedPlaying = false;
              if (this.props.t1) {
                const currentExpectedTime = clamp(
                  this.props.mediaStart + (this.props.t1.time() - this.props.playStart) * (Number.isFinite(this.props.speed) ? this.props.speed : 1),
                  this.props.mediaStart,
                  this.props.mediaEnd
                );
                const targetTime = this.props.reverse ? this.props.mediaEnd - (currentExpectedTime - this.props.mediaStart) : currentExpectedTime;
                const drift = Math.abs(this.mediaElRef.current.currentTime - targetTime);
                if (drift > 0.25) {
                  this.changeMediaTime(this.props.t1.time());
                }
              }
            }, 32);
          }).catch(() => {
            playResolved = true;
            clearTimeout(stallTimer);
            if (this._wasBufferingForPlay) {
              this._wasBufferingForPlay = false;
              this._isBufferingRequested = false;
              this.props.updateVideoBufferStatus({
                videoKey: this.props.id,
                isBuffering: false,
                videoSrc: this._baseVideoSrc(),
              });
            }
            this._justStartedPlaying = false;
          });
        } else if (!paused) {
          this._frameReady = undefined;
          this._startPlaybackMonitor();
          this.changeMediaTime(this.props.t1.time());
        } else {
          // FIX 4: Only register buffering if readyState is genuinely insufficient
          if (readyState < HTMLMediaElement.HAVE_CURRENT_DATA
            && !this.props.videoBufferStatus.has(this.props.id)
            && !this._isBufferingRequested) {
            this._isBufferingRequested = true;
            this.props.updateVideoBufferStatus({
              videoKey: this.props.id,
              isBuffering: true,
              bufferStartedAt: playhead,
              videoSrc: this._baseVideoSrc(),
            });
            this._wasBufferingForPlay = true;

            // FIX: Pause the video immediately when buffering starts
            if (this.mediaElRef.current && !this.mediaElRef.current.paused) {
              this.mediaElRef.current.pause();
            }
          }

          const startWaiting = () => {
            const onReady = () => {
              this.mediaElRef.current?.removeEventListener('seeked', onReady);
              this.mediaElRef.current?.removeEventListener('canplay', onReady);
              clearInterval(this._seekWaitInterval);
              attemptPlay();
            };
            this.mediaElRef.current.addEventListener('seeked', onReady, { once: true });
            this.mediaElRef.current.addEventListener('canplay', onReady, { once: true });

            this._attemptPlayStartTime = Date.now();
            this._seekWaitInterval = setInterval(attemptPlay, 100);
          };
          startWaiting();

          clearTimeout(this._seekSafetyTimeout);
          this._seekSafetyTimeout = setTimeout(() => {
            clearInterval(this._seekWaitInterval);
            this._frameReady = undefined;

            if (this._wasBufferingForPlay) {
              this._wasBufferingForPlay = false;
              this._isBufferingRequested = false;
              this.props.updateVideoBufferStatus({
                videoKey: this.props.id,
                isBuffering: false,
                videoSrc: this._baseVideoSrc(),
              });
            }

            // FIX 4: Final safety play — only if paused and has at least some data
            // FIX: Also check that no video is buffering
            const isBuffering = this.props.videoBufferStatus.size > 0;
            if (this.mediaElRef.current
              && this.mediaElRef.current.paused
              && this.mediaElRef.current.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
              && this.props.isPlaying
              && !this.props.isPaused
              && !isBuffering) {
              this.mediaElRef.current.play().catch(() => { });
            }
          }, 2500);
        }
      }
    } catch (error) { }
  }

  pause(playhead) {
    try {
      clearTimeout(this._pauseTimeout);

      if (this.props.fadeOutAudio) {
        this.props.fadeOutAudio({ id: this.props.id });
      }

      if (this.props.playAt - 20 <= playhead && playhead < this.props.playEnd) {
        if (this.mediaElRef.current.preload !== "auto") {
          this.preload = "auto";
          this.mediaElRef.current.preload = "auto";
          this.mediaElRef.current.src = addCacheClearQuery(this.props.src, this.props.allowCustomCaching, this.props.reverse);
          this.mediaElRef.current.load();
        }
      } else if (this.props.isPlaying) {
        if (this.mediaElRef.current.preload !== "auto") {
          this.preload = "auto";
          this.mediaElRef.current.preload = "auto";
          this.mediaElRef.current.src = addCacheClearQuery(this.props.src, this.props.allowCustomCaching, this.props.reverse);
          this.mediaElRef.current.load();
        }
      } else {
        this.preload = "none";
        this.mediaElRef.current.preload = "none";
        this.mediaElRef.current.removeAttribute("src");
        this.mediaElRef.current.load();
      }

      clearInterval(this.reverseTimer);
      clearInterval(this._seekWaitInterval);
      clearTimeout(this._seekSafetyTimeout);
      clearInterval(this._backSeekBufferTimer);
      this._stopPlaybackMonitor();

      this._backSeekBufferTimer = null;
      this._justStartedPlaying = false;

      // FIX 2: Only call pause() if video is actually playing — prevents pause() spam
      if (!this.mediaElRef.current.paused) {
        if (this.props.fadeOutAudio) {
          this.props.fadeOutAudio({ id: this.props.id });
          this._pauseTimeout = setTimeout(() => {
            if (this.mediaElRef.current) this.mediaElRef.current.pause();
          }, 20);
        } else {
          this.mediaElRef.current.pause();
        }
      }

      // FIX 3: Only seek on pause if meaningfully out of position
      const { mediaStart, mediaEnd, playStart, speed = 1, loop = false, reverse = false } = this.props;
      const safeSpeed = Number.isFinite(speed) ? speed : 1;
      const loopDuration = (mediaEnd - mediaStart) / safeSpeed;
      let loopPlayhead = playhead - playStart;
      if (loop) {
        const currentLoop = Math.max(Math.floor(loopPlayhead / loopDuration), 0);
        loopPlayhead -= currentLoop * loopDuration;
      }
      let expectedTime = clamp(mediaStart + loopPlayhead * safeSpeed, mediaStart, mediaEnd);
      if (reverse) expectedTime = mediaEnd - (expectedTime - mediaStart);

      if (this.mediaElRef.current) {
        const currentVideoTime = this.mediaElRef.current.currentTime;
        if (!Number.isFinite(currentVideoTime) || Math.abs(currentVideoTime - expectedTime) > this._SEEK_THRESHOLD) {
          this.changeMediaTime(playhead);
        }
      }
    } catch (error) { }
  }

  playPauseTweenCallback(isPausedSeek) {
    const playhead = this.props.t1.time();

    // FIX 1: Skip callback entirely if not near the play range
    if (!this._isNearPlayRange(playhead)) return;

    // FIX: Don't trigger play during buffering
    const isBuffering = this.props.videoBufferStatus.size > 0;

    if (playhead >= this.props.pauseAt || playhead < this.props.playAt || this.props.isPaused) {
      this.pause(playhead);
    } else if (!isPausedSeek && !isBuffering) {
      this.play(playhead);
    }
  }

  prefetchMedia() {
    try {
      if (this._prefetchedToken === this.props.prefetchToken) {
        return;
      }
      this._prefetchedToken = this.props.prefetchToken;

      if (this.props.item?.get?.("skipVideoElement")) {
        this.props.prefetchComplete(this.props.prefetchToken);
        return;
      }

      this.props.addTween({ callBack: this.playPauseTweenCallback, startTime: this.props.playAt, dataArr: [false] }, "addCallback");
      this.props.addTween({ callBack: this.playPauseTweenCallback, startTime: this.props.pauseAt, dataArr: [false] }, "addCallback");
      if (this.props.playAt - 19 > 0) {
        this.props.addTween({ callBack: this.playPauseTweenCallback, startTime: this.props.playAt - 19, dataArr: [true] }, "addCallback");
      }
      const preSeekTime = this._adaptivePreSeekTime();
      if (this.props.playAt > preSeekTime) {
        this.props.addTween({ callBack: this._preSeekForSceneTransition, startTime: this.props.playAt - preSeekTime, dataArr: [] }, "addCallback");
      } else if (this.props.playAt > 0) {
        this.props.addTween({ callBack: this._preSeekForSceneTransition, startTime: 0.01, dataArr: [] }, "addCallback");
      }
      if (this.props.maybeWebkit && this.props.from === "workspace" && this.props.pauseAt < this.props.playEnd) {
        this.props.addTween({ callBack: this.playPauseTweenCallback, startTime: this.props.playEnd, dataArr: [true] }, "addCallback");
      }

      let tries = 0;
      if (this.props.playhead > this.props.playEnd || this.props.playhead - this.props.playStart < -10) {
        this.props.prefetchComplete(this.props.prefetchToken);
      } else if (this.checkMediaHasEnoughData({ useReadyState: true })) {
        const seekPlayhead = this.props.runningState
          ? this.props.runningState.get("seekPlayhead") || 0
          : 0;
        const isWithinRange = seekPlayhead >= this.props.playAt && seekPlayhead < this.props.pauseAt;

        let needsSeek = false;
        if (isWithinRange && this.mediaElRef.current) {
          const { mediaStart, mediaEnd, playStart, speed = 1, loop = false, reverse = false } = this.props;
          const loopDuration = (mediaEnd - mediaStart) / (Number.isFinite(speed) ? speed : 1);
          let loopPlayhead = seekPlayhead - playStart;
          if (loop) {
            const currentLoop = Math.max(Math.floor(loopPlayhead / loopDuration), 0);
            loopPlayhead -= currentLoop * loopDuration;
          }
          let expectedTime = clamp(mediaStart + loopPlayhead * (Number.isFinite(speed) ? speed : 1), mediaStart, mediaEnd);
          if (reverse) {
            expectedTime = mediaEnd - (expectedTime - mediaStart);
          }
          const currentVideoTime = this.mediaElRef.current.currentTime;
          needsSeek = !Number.isFinite(currentVideoTime) || Math.abs(currentVideoTime - expectedTime) > 0.15;
        }

        if (needsSeek) {
          // If we need to seek, fall through to the else branch to wait for the seeked event
        } else {
          if (isWithinRange) {
            this.changeMediaTime(seekPlayhead);
          }
          this.props.prefetchComplete(this.props.prefetchToken);
        }
      } else {
        clearInterval(this.mediaPrefetchTimer);
        const { prefetchToken } = this.props;

        let prefetchCompleted = false;
        const completePrefetch = () => {
          if (prefetchCompleted) return;
          prefetchCompleted = true;
          clearInterval(this.mediaPrefetchTimer);
          this.mediaPrefetchTimer = null;
          if (this._prefetchCanplayHandler && this.mediaElRef.current) {
            this.mediaElRef.current.removeEventListener("canplay", this._prefetchCanplayHandler);
            this._prefetchCanplayHandler = null;
          }
          if (prefetchToken !== this.props.prefetchToken) return;
          if (!this.mediaElRef.current) return;

          const seekPlayhead = this.props.runningState
            ? this.props.runningState.get("seekPlayhead") || 0
            : 0;
          const isWithinRange = seekPlayhead >= this.props.playAt && seekPlayhead < this.props.pauseAt;

          const finalize = () => {
            if (prefetchToken !== this.props.prefetchToken) return;
            if (!this.mediaElRef.current) return;

            // FIX: Don't call play() during prefetch if buffering is active
            const isBuffering = this.props.videoBufferStatus.size > 0;
            if (isBuffering) {
              // Complete prefetch without calling play()
              this.props.prefetchComplete(this.props.prefetchToken);
              return;
            }

            // Strict prefetch for reframe scenes: skip play() entirely to avoid
            // background playback and audio leaks during loading. Normal videos
            // still do the legacy play+pause dance for browser codec warm-up.
            if (this._isReframeScene()) {
              // eslint-disable-next-line no-console
              this.props.prefetchComplete(this.props.prefetchToken);
              return;
            }

            this.mediaElRef.current.muted = true;
            this.mediaElRef.current
              .play()
              .catch(() => { })
              .finally(() => {
                if (prefetchToken === this.props.prefetchToken) {
                  if (this.mediaElRef.current) {
                    this.mediaElRef.current.playbackRate = 0;
                    this.mediaElRef.current.muted = false;
                  }
                  this.props.prefetchComplete(this.props.prefetchToken);
                }
              });
          };

          if (isWithinRange) {
            this.changeMediaTime(seekPlayhead);
            const video = this.mediaElRef.current;
            if (video.seeking) {
              const onSeeked = () => {
                video.removeEventListener("seeked", onSeeked);
                finalize();
              };
              video.addEventListener("seeked", onSeeked, { once: true });
              setTimeout(() => {
                video.removeEventListener("seeked", onSeeked);
                finalize();
              }, 1000);
            } else {
              finalize();
            }
          } else {
            finalize();
          }
        };

        this._prefetchCanplayHandler = () => { completePrefetch(); };
        if (this.mediaElRef.current) {
          this.mediaElRef.current.addEventListener("canplay", this._prefetchCanplayHandler, { once: true });
        }

        this.mediaPrefetchTimer = setInterval(() => {
          if (this.checkMediaHasEnoughData({ useReadyState: true }) || tries >= this.prefetchTries) {
            completePrefetch();
          }
          tries += 1;
        }, this.prefetchCheckInterval);
      }
    } catch (error) { }
  }

  isTimePropChanged(prevProps, currentProps) {
    return (
      prevProps.playStart !== currentProps.playStart
      || prevProps.playEnd !== currentProps.playEnd
      || prevProps.mediaStart !== currentProps.mediaStart
      || prevProps.mediaEnd !== currentProps.mediaEnd
      || prevProps.speed !== currentProps.speed
      || prevProps.loop !== currentProps.loop
      || prevProps.reverse !== currentProps.reverse
      || prevProps.playhead !== currentProps.playhead
    );
  }

  /**
    * Returns an adaptive buffer-ahead duration based on the video's native resolution.
    * Higher resolution videos require more buffered data to avoid premature stall detection.
    *
    * SD (< 720p):   1.0s  (default)
    * HD (720p):     1.5s
    * FHD (1080p):   2.0s
    * QHD (1440p):   2.5s
    * 4K+ (2160p+):  3.0s
    *
    * AI Reframe optimization: Use smaller buffer for reframe scenes (same source, less critical)
    */
  _adaptiveBufferDuration() {
    try {
      if (this.mediaElRef.current && this.props.mediaType === "video") {
        const { videoHeight } = this.mediaElRef.current;
        const isReframeScene = this._isReframeScene();
        const scaleFactor = isReframeScene ? 0.75 : 1.0; // Moderate buffer for reframe scenes (HQ reliability)

        if (Number.isFinite(videoHeight) && videoHeight > 0) {
          if (videoHeight >= 2160) return 3.0 * scaleFactor;
          if (videoHeight >= 1440) return 2.5 * scaleFactor;
          if (videoHeight >= 1080) return 2.0 * scaleFactor;
          if (videoHeight >= 720) return 1.5 * scaleFactor;
        }
      }
    } catch (e) { }
    return 0;
  }

  _adaptivePreSeekTime() {
    try {
      if (this.mediaElRef.current && this.props.mediaType === "video") {
        const { videoHeight } = this.mediaElRef.current;
        if (Number.isFinite(videoHeight) && videoHeight > 0) {
          if (videoHeight >= 2160) return 3.5;
          if (videoHeight >= 1440) return 3.0;
          if (videoHeight >= 1080) return 2.5;
          if (videoHeight >= 720) return 2.0;
        }
      }
    } catch (e) { }
    return 1.5;
  }

  _baseVideoSrc() {
    try {
      return this.props.src || "";
    } catch (e) {
      return "";
    }
  }

  _sameSrcAlreadyBuffering() {
    try {
      const baseSrc = this._baseVideoSrc();
      if (!baseSrc) return false;
      const bufferStatus = this.props.videoBufferStatus;
      let found = false;
      bufferStatus.forEach((val, key) => {
        if (key !== this.props.id && val && val.get && val.get("videoSrc") === baseSrc) {
          found = true;
        }
      });
      return found;
    } catch (e) {
      return false;
    }
  }

  _preSeekForSceneTransition() {
    try {
      if (!this.mediaElRef.current) return;
      if (!this.props.isPlaying) return; // Skip pre-seeking when paused/idle

      const { mediaStart, mediaEnd, reverse = false } = this.props;
      const targetTime = reverse ? mediaEnd : mediaStart;
      if (Number.isFinite(targetTime) && this.mediaElRef.current) {
        const currentVideoTime = this.mediaElRef.current.currentTime;
        const alreadyThere = Math.abs(currentVideoTime - targetTime) < 0.15;
        const hasData = this.mediaElRef.current.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
        if (alreadyThere && hasData) {
          this._frameReady = true;
          return;
        }

        const isReframeScene = this.props.item?.get?.("sceneData") && this.props.item?.get?.("originalId");
        const alreadyCloseEnough = isReframeScene && Math.abs(currentVideoTime - targetTime) < 0.5 && hasData;
        if (alreadyCloseEnough) {
          this._frameReady = true;
          return;
        }
      }

      const baseSrc = this._baseVideoSrc();
      if (baseSrc) {
        if (!window.__preSeekingVideos) window.__preSeekingVideos = new Map();
        const lastPreSeek = window.__preSeekingVideos.get(baseSrc);
        // AI Reframe optimization: Use longer debounce for reframe scenes (same source video)
        const isReframeScene = this._isReframeScene();
        const debounceTime = isReframeScene ? 200 : 2000;
        const layout = this.props.item?.getIn(["sceneData", "layout"]) || this.props.item?.get("layout");
        const isMultiPerson = layout === "SPLIT" || layout === "THREE" || layout === "FOUR" || layout === "SCREENSHARE" || layout === "GAMEPLAY";
        if (!isMultiPerson && lastPreSeek && lastPreSeek.id !== this.props.id && (Date.now() - (lastPreSeek._ts || 0)) < debounceTime) {
          // For reframe scenes, mark as ready quickly to avoid blocking
          if (isReframeScene) {
            this._frameReady = true;
          }
          return;
        }
        window.__preSeekingVideos.set(baseSrc, { id: this.props.id, _ts: Date.now() });
      }

      const video = this.mediaElRef.current;
      this._frameReady = false;

      if (video.preload !== "auto") {
        this.preload = "auto";
        video.preload = "auto";
        video.src = addCacheClearQuery(this.props.src, this.props.allowCustomCaching, this.props.reverse);
        video.load();
      }

      if (Number.isFinite(targetTime)) {
        video.removeEventListener("seeked", this._onPreSeekComplete);

        let frameConfirmed = false;

        this._onPreSeekComplete = () => {
          video.removeEventListener("seeked", this._onPreSeekComplete);

          if ("requestVideoFrameCallback" in video && !video.paused) {
            video.requestVideoFrameCallback(() => {
              if (!frameConfirmed) {
                frameConfirmed = true;
                this._frameReady = true;
              }
            });
          } else if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
            if (!frameConfirmed) {
              frameConfirmed = true;
              this._frameReady = true;
            }
          } else {
            setTimeout(() => {
              if (!frameConfirmed) {
                frameConfirmed = true;
                this._frameReady = true;
              }
            }, 50);
          }
        };

        video.addEventListener("seeked", this._onPreSeekComplete, { once: true });
        video.currentTime = targetTime;

        setTimeout(() => {
          video.removeEventListener("seeked", this._onPreSeekComplete);
          if (!frameConfirmed) {
            frameConfirmed = true;
            this._frameReady = true;
          }
        }, 1200);
      } else {
        this._frameReady = true;
      }
    } catch (e) {
      this._frameReady = true;
    }
  }

  checkMediaHasEnoughData(params = {}) {
    const { useReadyState, requiredReadyState = HTMLMediaElement.HAVE_CURRENT_DATA } = params;
    let { currentTime = null } = params;

    if (useReadyState) {
      try {
        if (this.mediaElRef.current) {
          return this.mediaElRef.current.readyState >= requiredReadyState;
        }
      } catch (error) { }
    } else {
      try {
        if (this.mediaElRef.current) {
          let bufferLeftDuration = 0;
          let bufferRightDuration = this._adaptiveBufferDuration();

          const { buffered, duration } = this.mediaElRef.current;

          if (!Number.isFinite(currentTime)) {
            currentTime = this.mediaElRef.current.currentTime;
          }

          if (duration >= 1 && duration - currentTime <= bufferRightDuration) {
            return true;
          }

          if (duration < 1) {
            bufferRightDuration = 0.1;
          }

          if (this.props.reverse) {
            if (this.props.maybeWebkit) {
              bufferLeftDuration = -1;
            } else {
              bufferLeftDuration = (this.reverseInterval / 1000) * 3;
            }
            bufferRightDuration = 0;
          }

          if (currentTime < bufferLeftDuration) bufferLeftDuration = currentTime;
          if (duration - currentTime < bufferRightDuration) bufferRightDuration = duration - currentTime;

          for (let i = 0; i < buffered.length; i += 1) {
            const start = buffered.start(i);
            const leftBuffered = ((currentTime - start) - bufferLeftDuration) >= -0.009;
            const rightBuffered = ((buffered.end(i) - currentTime) - bufferRightDuration) >= -0.009;
            const isBuffered = leftBuffered && rightBuffered;

            if (isBuffered) return true;
          }
        }
      } catch (error) { }
    }

    return false;
  }

  render() {
    const src = addCacheClearQuery(this.props.src, this.props.allowCustomCaching, this.props.reverse);
    const isWithinItemRange = this.props.playStart <= this.props.playhead && this.props.playhead <= this.props.playEnd;
    const canHandleLoop = this.props.isPlaying && this.props.loop && !this.props.reverse;
    const isNotReversePlay = this.props.isPlaying && !this.props.reverse;
    const willPrefetch = this.props.isPlaying && !(this.props.playhead > this.props.playEnd || this.props.playhead - this.props.playStart < -10);
    let media = null;
    const VideoComponent = this.props.isBackgroundRemoval ? TransparentVideo : CanvasVideo;

    let preload = "none";
    if (this.preload) {
      preload = this.preload;
    } else if (isWithinItemRange || willPrefetch) {
      preload = "auto";
    }

    const skipVideoElement = this.props.item?.get?.("skipVideoElement") === true;

    if (this.props.mediaType === "video" && skipVideoElement) {
      return null;
    }

    if (this.props.mediaType === "video") {
      media =
        this.props.isBackgroundRemoval || this.props.chromaKey ? (
          <>
            {!this.props.isPlayAll && (
              <CanvasImage
                className={this.props.className}
                style={this.props.style}
                src={this.props.poster}
                alt=""
                isBackgroundRemoval={this.props.isBackgroundRemoval}
                chromaKey={this.props.chromaKey}
                videoRef={this.imageRef}
                tolerance={this.props.tolerance}
              />
            )}
            <VideoComponent
              assignRef={this.assignRef}
              src={src}
              className={this.props.className}
              style={this.props.style}
              preload={preload}
              crossOrigin={this.props.crossOrigin || "anonymous"}
              isPlayAll={this.props.isPlayAll}
              onWaiting={this.onWaiting}
              mediaElRef={this.mediaElRef}
              isBackgroundRemoval={this.props.isBackgroundRemoval}
              chromaKey={this.props.chromaKey}
              imageRef={this.imageRef}
              tolerance={this.props.tolerance}
            />
          </>
        ) : (
          <>
            {!this.props.isPlaying && this.props.poster && !this.props.isAvatar && !this.props.item?.get("isTransparentAvatar") && !this.props.item?.get("sceneData") && (
              <img
                className={this.props.className}
                style={this.props.style}
                src={this.props.poster}
                alt=""
              />
            )}
            <video
              ref={this.assignRef}
              key={src}
              src={src}
              className={this.props.className}
              style={this.props.style}
              preload={preload}
              playsInline={true}
              crossOrigin={this.props.crossOrigin || "anonymous"}
              muted={this.props.reverse}
              onWaiting={
                isNotReversePlay
                  ? (this._isReframeScene() && this._sameSrcAlreadyBuffering()
                      ? undefined
                      : this.handleBuffering)
                  : undefined
              }
              onTimeUpdate={canHandleLoop ? this.handleLoop : undefined}
              onPause={canHandleLoop ? this.handleLoop : undefined}
              onError={this.props.isPlaying ? this.handleError : undefined}
            >
              <source src={src} type="video/mp4" />
              <source src={src} type="video/webm" />
              <track kind="captions" />
            </video>
          </>
        );
    } else if (this.props.mediaType === "audio") {
      media = (
        <audio
          ref={this.assignRef}
          className={this.props.className}
          preload={preload}
          crossOrigin={this.props.crossOrigin || "anonymous"}
          src={src}
          onWaiting={
            isNotReversePlay
              ? (this._isReframeScene() && this._sameSrcAlreadyBuffering()
                  ? undefined
                  : this.handleBuffering)
              : undefined
          }
          onError={this.props.isPlaying ? this.handleError : undefined}
        >
          <track kind="captions" />
        </audio>
      );
    }

    return media;
  }
}

PlayerMediaComponent.propTypes = {
  src: PropTypes.string,
  poster: PropTypes.string,
  playStart: PropTypes.number,
  playEnd: PropTypes.number,
  mediaStart: PropTypes.number,
  mediaEnd: PropTypes.number,
  playhead: PropTypes.number,
  isPlayAll: PropTypes.bool,
  mediaElRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  style: PropTypes.object,
  className: PropTypes.string,
  runningState: PropTypes.object,
  prefetchToken: PropTypes.string,
  prefetchComplete: PropTypes.func,
  setPlayAll: PropTypes.func,
  isLoaded: PropTypes.bool,
  addTween: PropTypes.func,
  videoBufferStatus: PropTypes.object,
  t1: PropTypes.object,
  updateVideoBufferStatus: PropTypes.func,
  fadeOutAudio: PropTypes.func,
  id: PropTypes.string,
  mediaType: PropTypes.oneOf(["video", "audio"]),
  crossOrigin: PropTypes.string,
  playPauseAudio: PropTypes.func,
  isPaused: PropTypes.bool,
  isBackgroundRemoval: PropTypes.bool,
  chromaKey: PropTypes.string,
  tolerance: PropTypes.number,
  isAvatar: PropTypes.bool,
  allowCustomCaching: PropTypes.bool,
  item: PropTypes.object,
  maybeWebkit: PropTypes.bool,
  isPlaying: PropTypes.bool,
  playAt: PropTypes.number,
  pauseAt: PropTypes.number,
  loop: PropTypes.bool,
  reverse: PropTypes.bool,
  swStatus: PropTypes.string,
  from: PropTypes.string,
  projectData: PropTypes.object,
  zoomFactor: PropTypes.number,
  propertyComponent: PropTypes.string,
};

const PlayerMediaContainer = (props) => {
  const {
    playStart,
    playEnd,
    playhead,
    speed = 1,
  } = props;
  let { playAt, pauseAt } = props;

  const playerAudioCallback = useContext(PlayerAudioCallbackContext);

  if (!Number.isFinite(playAt)) {
    playAt = playStart;
  }
  if (!Number.isFinite(pauseAt)) {
    pauseAt = playEnd;
  }

  if (!props.loop) {
    const mediaDuration = (props.mediaEnd - props.mediaStart) / speed;
    const playDuration = playEnd - playStart;
    if (mediaDuration < playDuration) {
      const newPlayEnd = playStart + mediaDuration;
      if (pauseAt > newPlayEnd) {
        pauseAt = newPlayEnd;
      }
    }
  }

  return (
    <PlayerMediaComponent
      {...props}
      playStart={playStart}
      playEnd={playEnd}
      playhead={playhead}
      playAt={playAt}
      pauseAt={pauseAt}
      mediaElRef={playerAudioCallback.assignMediaRef}
      playPauseAudio={playerAudioCallback.playPauseAudio}
      fadeOutAudio={playerAudioCallback.fadeOutAudio}
    />
  );
};

PlayerMediaContainer.propTypes = PlayerMediaComponent.propTypes;

const mapStateToProps = (state) => ({
  t1: state.app.get("t1"),
  runningState: state.app.get("runningState"),
  playhead: state.app.get("playhead"),
  isPlayAll: state.app.get("isPlayAll"),
  isPlaying: state.app.get("isPlayAll"),
  prefetchToken: state.app.get("prefetchToken"),
  isLoaded: state.app.get("isLoaded"),
  videoBufferStatus: state.app.get("videoBufferStatus"),
  isPaused: state.app.get("isPausePlayer"),
  swStatus: state.app.get("swStatus"),
  maybeWebkit: state.app.get("maybeWebkit"),
  projectData: state.projectDetails,
  zoomFactor: state.app.get("zoomFactor"),
  propertyComponent: state.app.getIn(["propertyWindow", "component"]),
  isMobileView: state.app.get("isMobileView"),
});

const mapDispatchToProps = (dispatch) => ({
  prefetchComplete: (token, count) => dispatch(prefetchComplete(token, count)),
  addTween: (data, tweenType) => dispatch(addTween(data, tweenType)),
  setPlayAll: (data) => dispatch(setPlayAll(data)),
  updateVideoBufferStatus: (data) => dispatch(updateVideoBufferStatus(data)),
});

const PlayerMedia = connect(
  mapStateToProps,
  mapDispatchToProps
)(PlayerMediaContainer);

export default PlayerMedia;